// src/components/NewMintNFTComponentForm.js
import React, { useEffect, useRef, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { storage, auth, db } from '../firebaseConfig';
import { useCreatorPublishing } from '../hooks/useCreatorPublishing';
import NFTMetadataFields from './NFTMetadataFields';

export default function NewMintNFTComponentForm({
                                                    name = '',
                                                    description = '',
                                                    onNameChange,
                                                    onDescriptionChange,
                                                    userId,
                                                    ensureAuthUid,
                                                    collectionId = '',
                                                    initialTemplateId = '',
                                                    initialCreatorFeeSol = 0,
                                                    initialRoyalties = null,

                                                    // Save-only callback (templateId, creatorFeeSol, royalties)
                                                    onUploadComplete,

                                                    // NEW props for 3-button footer
                                                    enablePublishButton = false,
                                                    onSubmitAndPublish,

                                                    onCancel,
                                                    error,
                                                }) {
    const [file, setFile] = useState(null);
    const [sourceImageUrl, setSourceImageUrl] = useState('');
    const [formError, setFormError] = useState('');
    const { collections = [] } = useCreatorPublishing(userId || '');
    const appliedDefaults = useRef('');
    const dirty = useRef({});
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [attributes, setAttributes] = useState([]);
    const [creatorFeeSol, setCreatorFeeSol] = useState(initialCreatorFeeSol ?? 0); // paid at mint to SPACE_OWNER

    // Royalties: total % (0–20). SPACE_OWNER is included automatically (remainder).
    const [royaltyPercent, setRoyaltyPercent] = useState(initialRoyalties?.percentage ?? 7);
    const [otherCreators, setOtherCreators] = useState(() => (initialRoyalties?.creators || []).filter(creator => creator.address !== 'SPACE_OWNER' && creator.address !== userId)); // [{address, percentage}]

    useEffect(() => {
        const selected = collections.find(item => item.id === collectionId);
        if (initialTemplateId || !selected || appliedDefaults.current === collectionId) return;
        appliedDefaults.current = collectionId;
        if (!dirty.current.name) onNameChange(selected.name || '');
        if (!dirty.current.description) onDescriptionChange(selected.description || '');
        if (!dirty.current.image) setSourceImageUrl(selected.image || '');
        if (!dirty.current.attributes) setAttributes((selected.attributes || []).map(attribute => ({ name: attribute.trait_type || attribute.name || '', value: attribute.value ?? '' })));
        if (!dirty.current.royalties && selected.creatorConfiguration) {
            setRoyaltyPercent(selected.creatorConfiguration.basisPoints / 100);
            setOtherCreators((selected.creatorConfiguration.creators || []).filter(creator => creator.address !== userId));
        }
    }, [collectionId, collections, initialTemplateId, onNameChange, onDescriptionChange, userId]);
    useEffect(() => {
        if (!initialTemplateId) return undefined;
        let active = true;
        getDoc(doc(db, 'nftMetadataTemplates', initialTemplateId)).then(snapshot => {
            if (!active) return;
            const template = snapshot.data();
            if (!template || template.userId !== userId) throw new Error('The original NFT template could not be loaded.');
            if (!dirty.current.name) onNameChange(template.name || '');
            if (!dirty.current.description) onDescriptionChange(template.description || '');
            if (!dirty.current.image) setSourceImageUrl(template.imageUrl || '');
            if (!dirty.current.attributes) setAttributes((template.attributes || []).map(attribute => ({ name: attribute.name || attribute.trait_type || '', value: attribute.value ?? '' })));
        }).catch(failure => { if (active) setFormError(failure.message); });
        return () => { active = false; };
    }, [initialTemplateId, userId, onNameChange, onDescriptionChange]);

    const othersTotalPct = otherCreators.reduce(
        (s, r) => s + (Number.isFinite(+r.percentage) ? Number(r.percentage) : 0),
        0
    );
    const ownerPct = Math.max(0, 100 - Math.min(othersTotalPct, 100));

    const handleMetadataChange = patch => {
        Object.keys(patch).forEach(key => { dirty.current[key] = true; });
        if (patch.name !== undefined) onNameChange(patch.name);
        if (patch.description !== undefined) onDescriptionChange(patch.description);
        if (patch.attributes !== undefined) setAttributes(patch.attributes);
    };

    const handleFileSelect = (e) => {
        const f = e.target.files?.[0];
        if (f) { dirty.current.image = true; setFile(f); }
    };

    const updateOtherCreator = (i, field, value) => {
        dirty.current.royalties = true;
        setOtherCreators((prev) =>
            prev.map((r, idx) =>
                idx === i ? { ...r, [field]: field === 'percentage' ? Number(value) : value } : r
            )
        );
    };
    const addOtherCreator = () => { dirty.current.royalties = true; setOtherCreators((prev) => [...prev, { address: '', percentage: 0 }]); };
    const removeOtherCreator = (i) => { dirty.current.royalties = true; setOtherCreators((prev) => prev.filter((_, idx) => idx !== i)); };

    const canSave =
        !!(file || sourceImageUrl) &&
        !!name.trim() &&
        !!description.trim() &&
        Number.isFinite(royaltyPercent) &&
        royaltyPercent >= 0 &&
        royaltyPercent <= 20 &&
        othersTotalPct <= 100;

    /**
     * Uploads image to Storage and creates a template in Firestore.
     * Resolves { templateId, safeCreatorFee, royalties }.
     */
    const performUploadAndSaveTemplate = () =>
        new Promise(async (resolve, reject) => {
            try {
                if (!canSave) return reject(new Error('invalid-form'));
                setUploading(true);

                let uid = (userId || auth.currentUser?.uid || '').trim();
                if (typeof ensureAuthUid === 'function') {
                    try {
                        const ensuredUid = await ensureAuthUid();
                        if (ensuredUid) uid = String(ensuredUid).trim();
                    } catch (authError) {
                        setUploading(false);
                        return reject(authError);
                    }
                }
                if (!uid) {
                    setUploading(false);
                    return reject(new Error('User not authenticated for upload.'));
                }
                if (auth?.currentUser?.uid && auth.currentUser.uid !== uid) {
                    setUploading(false);
                    return reject(new Error('Authenticated user does not match upload owner.'));
                }
                // Preserve the legacy mint upload folder so existing deployed rules continue to match.
                let image = file;
                if (!image) {
                    const response = await fetch(sourceImageUrl, { mode: 'cors' });
                    if (!response.ok) throw new Error('The default artwork could not be loaded. Choose an image file.');
                    image = await response.blob();
                }
                const path = `nftMetadataImages/${uid}/${Date.now()}_${file?.name || 'image'}`;
                const storageRef = ref(storage, path);
                const task = uploadBytesResumable(storageRef, image);

                task.on(
                    'state_changed',
                    null,
                    (err) => {
                        console.error(err);
                        setUploading(false);
                        reject(err);
                    },
                    async () => {
                        try {
                            const imageUrl = await getDownloadURL(task.snapshot.ref);
                            setUploading(false);
                            setSaving(true);

                            // Save template doc
                            const payload = {
                                userId: uid,
                                name,
                                description,
                                imageUrl,
                                attributes,
                                createdAt: serverTimestamp(),
                            };
                            const docRef = await addDoc(collection(db, 'nftMetadataTemplates'), payload);

                            // Build royalties payload for component config
                            const royalties = {
                                percentage: Math.min(20, Math.max(0, Number(royaltyPercent) || 0)), // 0..20
                                creators: [
                                    // SPACE_OWNER is implied by the mint component; included here for the editor's clarity
                                    { address: 'SPACE_OWNER', percentage: ownerPct },
                                    ...otherCreators.filter(r => Number(r.percentage) > 0).map((r) => ({
                                        address: (r.address || '').trim(),
                                        percentage: Math.floor(Number(r.percentage) || 0),
                                    })),
                                ],
                            };

                            const safeCreatorFee = Math.max(0, Number(creatorFeeSol) || 0);

                            resolve({ templateId: docRef.id, safeCreatorFee, royalties });
                        } catch (e) {
                            console.error(e);
                            reject(e);
                        } finally {
                            setSaving(false);
                        }
                    }
                );
            } catch (e) {
                console.error(e);
                setUploading(false);
                setSaving(false);
                reject(e);
            }
        });

    const handleSave = async () => {
        try {
            const { templateId, safeCreatorFee, royalties } = await performUploadAndSaveTemplate();
            onUploadComplete?.(templateId, safeCreatorFee, royalties);
        } catch (failure) {
            setFormError(failure.message || 'The NFT template could not be saved.');
        }
    };

    const handleSaveAndPublish = async () => {
        try {
            const { templateId, safeCreatorFee, royalties } = await performUploadAndSaveTemplate();
            onSubmitAndPublish?.(templateId, safeCreatorFee, royalties);
        } catch (failure) {
            setFormError(failure.message || 'The NFT template could not be published.');
        }
    };

    return (
        <div className="new-component-form mint-form">
            <h1>NFT Minter Component</h1>
            <h3>Configure an NFT Template</h3>
            {collectionId && <p>Collection details are editable starting points. Change the artwork, name, description, or attributes for this NFT.</p>}

            <NFTMetadataFields name={name} description={description} attributes={attributes}
                onChange={handleMetadataChange} />

            <label className="muulti-label" style={{ marginTop: 8 }}>
                Artwork Image
                {!file && sourceImageUrl && <img src={sourceImageUrl} alt="Default artwork — choose a file to replace it" style={{ maxWidth: 220, maxHeight: 160, objectFit: 'contain' }} />}
                <input type="file" accept="image/*" onChange={handleFileSelect} className="muulti-input" />
            </label>

            {/* Creator fee (paid at mint to SPACE_OWNER) */}
            <label className="muulti-label" style={{ marginTop: 12 }}>
                Collector price (SOL)
                <input
                    className="muulti-input"
                    type="number"
                    min="0"
                    step="0.001"
                    value={creatorFeeSol}
                    onChange={(e) => setCreatorFeeSol(e.target.value)}
                    placeholder="e.g. 0.05"
                />
                <small>Advertised mint price. Any configured commission is deducted from creator proceeds.</small>
            </label>

            {/* Royalties editor */}
            <div style={{ marginTop: 16 }}>
                <h4>Royalties (secondary sales)</h4>

                <label className="muulti-label" style={{ display: 'block' }}>
                    Total Royalties (%)
                    <input
                        className="muulti-input"
                        type="number"
                        min="0"
                        max="20"
                        step="0.1"
                        value={royaltyPercent}
                        onChange={(e) => { dirty.current.royalties = true; setRoyaltyPercent(Number(e.target.value)); }}
                    />
                </label>
                <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                    {royaltyPercent}% total on secondary sales. The MuultiSpace creator (<code>SPACE_OWNER</code>) is always
                    included and cannot be replaced. Other recipients must sum to ≤ 100%. The creator receives the remainder.
                </div>

                {/* Show derived SPACE_OWNER percentage for clarity */}
                <div style={{ marginTop: 12, padding: 8, border: '1px solid #eee', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>SPACE_OWNER (auto)</div>
                    <div style={{ fontSize: 13, opacity: 0.8 }}>
                        Percentage: <strong>{ownerPct}%</strong> (100% − sum(other creators))
                    </div>
                </div>

                {/* Other creators */}
                <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                    <div style={{ fontWeight: 600 }}>Other creators</div>
                    {otherCreators.map((r, i) => (
                        <div
                            key={i}
                            style={{ display: 'grid', gridTemplateColumns: '1fr 120px 92px', gap: 8, alignItems: 'center' }}
                        >
                            <input
                                className="muulti-input"
                                placeholder="Recipient public key"
                                value={r.address}
                                onChange={(e) => updateOtherCreator(i, 'address', e.target.value)}
                            />
                            <input
                                className="muulti-input"
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={r.percentage}
                                onChange={(e) => updateOtherCreator(i, 'percentage', e.target.value)}
                            />
                            <button className="muulti-button cancel-button" onClick={() => removeOtherCreator(i)}>
                                Remove
                            </button>
                        </div>
                    ))}
                    <button className="muulti-button" onClick={addOtherCreator}>
                        Add Creator
                    </button>
                    <div style={{ fontSize: 12, color: othersTotalPct <= 100 ? '#16a34a' : '#b91c1c' }}>
                        Sum of other creators: <b>{othersTotalPct}%</b> (must be ≤ 100%)
                    </div>
                </div>
            </div>

            <div className="muulti-container-button-container" style={{ marginTop: 12 }}>
                <button onClick={handleSave} disabled={uploading || saving || !canSave} className="muulti-button">
                    {uploading ? 'Uploading…' : saving ? 'Saving…' : 'Save Panel'}
                </button>

                {enablePublishButton && (
                    <button
                        onClick={handleSaveAndPublish}
                        disabled={uploading || saving || !canSave}
                        className="muulti-button"
                    >
                        {uploading ? 'Uploading…' : saving ? 'Saving…' : 'Save Panel & Publish'}
                    </button>
                )}

                <button onClick={onCancel} className="muulti-button cancel-button">
                    Cancel
                </button>
            </div>

            {error && <p className="error">{error}</p>}
            {formError && <p className="error" role="alert">{formError}</p>}
        </div>
    );
}
